import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  Box,
  Text,
  Button,
  Modal,
  TextInput,
  Textarea,
  Checkbox,
  Group,
  Stack,
  FileInput,
  Alert,
  Loader,
  Tabs,
  Grid,
  Card,
  Badge,
  SimpleGrid,
  Progress,
  Dialog
} from '@mantine/core'
import { FiUpload, FiInfo, FiTrash2, FiHeart, FiShare2, FiMessageSquare, FiCornerDownRight, FiSend } from 'react-icons/fi'
import { useHistory } from 'react-router-dom'
import agent from '../../app/api/agent'
import { toAbsoluteMediaUrl } from '../../app/api/resolveApiBaseUrl'
import { useLegalText } from '../../app/hooks/useLegalText'
import type { SlopPostDto, SlopPostCommentDto } from '../../app/models/slopPost'
import { store } from '../../app/stores/store'
import SlopFeed from '../../components/SlopFeed'

/** Matches SlopFeed API and nginx upload cap (150 MiB). */
const MAX_SLOP_VIDEO_BYTES = 150 * 1024 * 1024

export default function SlopFeedPage () {
  const isAdmin = store.userStore.user?.isAdmin ?? false
  const isLoggedIn = !!store.userStore.user
  const history = useHistory()

  // Upload Modal State
  const [uploadModalOpen, setUploadModalOpen] = useState(false)
  const [uploadLoading, setUploadLoading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [videoFile, setVideoFile] = useState<File | null>(null)
  const [uploadTitle, setUploadTitle] = useState('')
  const [uploadDescription, setUploadDescription] = useState('')
  const [consentGiven, setConsentGiven] = useState(false)

  // Embed Tab State
  const [embedUrl, setEmbedUrl] = useState('')
  const [embedTitle, setEmbedTitle] = useState('')
  const [embedDescription, setEmbedDescription] = useState('')
  const [embedLoading, setEmbedLoading] = useState(false)
  const [embedError, setEmbedError] = useState<string | null>(null)

  // Feed State
  const [feedPosts, setFeedPosts] = useState<SlopPostDto[]>([])
  const [feedLoading, setFeedLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)

  // Detail Modal State
  const [selectedPost, setSelectedPost] = useState<SlopPostDto | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)

  // Delete Confirmation State
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)
  const [deleteLoading, setDeleteLoading] = useState(false)

  // Comment State
  const [comments, setComments] = useState<SlopPostCommentDto[]>([])
  const [commentsLoading, setCommentsLoading] = useState(false)
  const [newComment, setNewComment] = useState('')
  const [commentSubmitting, setCommentSubmitting] = useState(false)
  const [replyingToId, setReplyingToId] = useState<number | null>(null)
  const [replyText, setReplyText] = useState('')

  // Share State
  const [shareCopied, setShareCopied] = useState(false)

  // Consent Text
  const { text: consentText, loading: consentLoading } = useLegalText('slop-feed-consent')

  // Intersection Observer for infinite scroll
  const sentinelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    loadInitialFeed()
  }, [])

  // Infinite scroll observer
  useEffect(() => {
    if (!sentinelRef.current || !hasMore || feedLoading) return

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && hasMore && !feedLoading) {
        loadMoreFeed()
      }
    })

    observer.observe(sentinelRef.current)
    return () => observer.disconnect()
  }, [feedLoading, hasMore])

  const loadInitialFeed = async () => {
    setFeedLoading(true)
    try {
      const posts = await agent.SlopFeed.getFeed(1, 20)
      setFeedPosts(posts || [])
      setCurrentPage(1)
      setHasMore((posts?.length ?? 0) === 20)
    } catch (err) {
      console.error('Failed to load feed:', err)
    } finally {
      setFeedLoading(false)
    }
  }

  const loadMoreFeed = async () => {
    const nextPage = currentPage + 1
    setFeedLoading(true)
    try {
      const posts = await agent.SlopFeed.getFeed(nextPage, 20)
      if (posts && posts.length > 0) {
        setFeedPosts((prev) => [...prev, ...posts])
        setCurrentPage(nextPage)
        setHasMore(posts.length === 20)
      } else {
        setHasMore(false)
      }
    } catch (err) {
      console.error('Failed to load more posts:', err)
    } finally {
      setFeedLoading(false)
    }
  }

  const handleUpload = async () => {
    setUploadError(null)

    if (!videoFile) {
      setUploadError('Please select a video file')
      return
    }

    if (!uploadTitle.trim()) {
      setUploadError('Please enter a title')
      return
    }

    if (!consentGiven) {
      setUploadError('You must agree to the consent terms')
      return
    }

    if (videoFile.size > MAX_SLOP_VIDEO_BYTES) {
      setUploadError('Video exceeds 150 MB limit')
      return
    }

    setUploadLoading(true)
    try {
      const formData = new FormData()
      formData.append('Video', videoFile)
      formData.append('Title', uploadTitle)
      formData.append('Description', uploadDescription)
      formData.append('ConsentAccepted', 'true')
      formData.append('ConsentText', consentText?.Body || '')
      formData.append('ConsentTextVersion', String(consentText?.Version || 1))

      await agent.SlopFeed.upload(formData)

      // Reset form
      setVideoFile(null)
      setUploadTitle('')
      setUploadDescription('')
      setConsentGiven(false)
      setUploadModalOpen(false)

      // Reload feed
      await loadInitialFeed()
    } catch (err: any) {
      setUploadError(err.message || 'Failed to upload video')
    } finally {
      setUploadLoading(false)
      setUploadProgress(0)
    }
  }

  const handleAddEmbed = async () => {
    setEmbedError(null)

    if (!embedUrl.trim()) {
      setEmbedError('Please enter an embed URL')
      return
    }

    if (!embedTitle.trim()) {
      setEmbedError('Please enter a title')
      return
    }

    setEmbedLoading(true)
    try {
      await agent.SlopFeed.addEmbed({
        embedSourceUrl: embedUrl,
        title: embedTitle,
        description: embedDescription
      })

      setEmbedUrl('')
      setEmbedTitle('')
      setEmbedDescription('')
      await loadInitialFeed()
    } catch (err: any) {
      setEmbedError(err.message || 'Failed to add embed')
    } finally {
      setEmbedLoading(false)
    }
  }

  const handleDeletePost = async (id: string) => {
    setDeleteLoading(true)
    try {
      await agent.SlopFeed.deletePost(id)
      setFeedPosts((prev) => prev.filter((p) => p.id !== id))
      setDeleteConfirmId(null)
      setSelectedPost(null)
    } catch (err) {
      console.error('Failed to delete post:', err)
    } finally {
      setDeleteLoading(false)
    }
  }

  const handleToggleLike = async (id: string) => {
    try {
      const result = await agent.SlopFeed.toggleLike(id)
      setFeedPosts((prev) =>
        prev.map((p) =>
          p.id === id
            ? {
                ...p,
                likeCount: result.newLikeCount,
                isLikedByCurrentUser: result.isLiked
              }
            : p
        )
      )
      if (selectedPost?.id === id) {
        setSelectedPost({
          ...selectedPost,
          likeCount: result.newLikeCount,
          isLikedByCurrentUser: result.isLiked
        })
      }
    } catch (err) {
      console.error('Failed to toggle like:', err)
    }
  }

  // Load comments when a post is opened
  useEffect(() => {
    if (!selectedPost) { setComments([]); setNewComment(''); setReplyingToId(null); setReplyText(''); return }
    setCommentsLoading(true)
    agent.SlopFeed.getComments(selectedPost.id)
      .then(setComments)
      .catch(() => setComments([]))
      .finally(() => setCommentsLoading(false))
  }, [selectedPost?.id])

  const requireAuth = (action: () => void) => {
    if (!isLoggedIn) { history.push('/register'); return }
    action()
  }

  const handleShare = async (post: SlopPostDto) => {
    const url = `${window.location.origin}/slop-feed/${post.id}`
    if (navigator.share) {
      try { await navigator.share({ title: post.title || 'GunSlop Video', url }) } catch {}
    } else {
      await navigator.clipboard.writeText(url)
      setShareCopied(true)
      setTimeout(() => setShareCopied(false), 2000)
    }
  }

  const handleAddComment = async (postId: string) => {
    if (!newComment.trim()) return
    setCommentSubmitting(true)
    try {
      const created = await agent.SlopFeed.addComment(postId, newComment.trim())
      setComments((prev) => [...prev, created])
      setNewComment('')
      setFeedPosts((prev) => prev.map((p) => p.id === postId ? { ...p, commentCount: p.commentCount + 1 } : p))
      if (selectedPost?.id === postId) { setSelectedPost((p) => p ? { ...p, commentCount: p.commentCount + 1 } : p) }
    } catch (err) {
      console.error('Failed to add comment:', err)
    } finally {
      setCommentSubmitting(false)
    }
  }

  const handleAddReply = async (postId: string, parentId: number) => {
    if (!replyText.trim()) return
    setCommentSubmitting(true)
    try {
      const created = await agent.SlopFeed.addComment(postId, replyText.trim(), parentId)
      setComments((prev) =>
        prev.map((c) => c.id === parentId ? { ...c, replies: [...c.replies, created] } : c)
      )
      setReplyText('')
      setReplyingToId(null)
      setFeedPosts((prev) => prev.map((p) => p.id === postId ? { ...p, commentCount: p.commentCount + 1 } : p))
      if (selectedPost?.id === postId) { setSelectedPost((p) => p ? { ...p, commentCount: p.commentCount + 1 } : p) }
    } catch (err) {
      console.error('Failed to add reply:', err)
    } finally {
      setCommentSubmitting(false)
    }
  }

  const handleDeleteComment = async (postId: string, commentId: number, parentId?: number) => {
    try {
      await agent.SlopFeed.deleteComment(postId, commentId)
      if (parentId != null) {
        setComments((prev) =>
          prev.map((c) => c.id === parentId ? { ...c, replies: c.replies.filter((r) => r.id !== commentId) } : c)
        )
      } else {
        setComments((prev) => prev.filter((c) => c.id !== commentId))
      }
      setFeedPosts((prev) => prev.map((p) => p.id === postId ? { ...p, commentCount: Math.max(0, p.commentCount - 1) } : p))
      if (selectedPost?.id === postId) { setSelectedPost((p) => p ? { ...p, commentCount: Math.max(0, p.commentCount - 1) } : p) }
    } catch (err) {
      console.error('Failed to delete comment:', err)
    }
  }

  const formatDate = (date: string | Date) => {
    const d = new Date(date)
    const now = new Date()
    const diffMs = now.getTime() - d.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return d.toLocaleDateString()
  }

  return (
    <Box sx={{ maxWidth: 1400, margin: '0 auto', padding: '24px' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200,
          gap: 16,
          textAlign: 'center',
          marginBottom: 40
        }}
      >
        <Text
          sx={{
            fontFamily: 'Oswald, sans-serif',
            fontWeight: 700,
            fontSize: 48,
            letterSpacing: 4,
            color: 'var(--gs-accent)',
            lineHeight: 1
          }}
        >
          SLOP FEED
        </Text>
        <Text
          sx={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 18,
            color: 'var(--gs-muted)',
            maxWidth: 560
          }}
        >
          Use the <strong>Discover</strong> tab for the mixed feed (ephemeral shoots from the top
          nav upload, blogs, and hosted clips). <strong>Video library</strong> is long-form uploads
          from the button below. The nav upload opens the shoots editor (pinch to resize text) before
          posting.
        </Text>
        <Button
          onClick={() => setUploadModalOpen(true)}
          sx={{
            marginTop: 16,
            background: 'rgba(232,93,4,0.15)',
            border: '1px solid rgba(232,93,4,0.35)',
            color: 'var(--gs-accent)',
            '&:hover': { background: 'rgba(232,93,4,0.25)' }
          }}
          leftIcon={<FiUpload size={16} />}
        >
          Upload Video
        </Button>
      </Box>

      {/* Tabs */}
      <Tabs defaultValue="discover" sx={{ marginBottom: 40 }}>
        <Tabs.List>
          <Tabs.Tab value="discover">Discover</Tabs.Tab>
          <Tabs.Tab value="library">Video library</Tabs.Tab>
          {isAdmin && <Tabs.Tab value="embed">Embed a Video</Tabs.Tab>}
        </Tabs.List>

        <Tabs.Panel value="discover" pt="md">
          <SlopFeed />
        </Tabs.Panel>

        {/* Long-form SlopPost grid (GET /api/slopfeed) — not the same as nav-bar shoot uploads */}
        <Tabs.Panel value="library">
          {feedLoading && feedPosts.length === 0
            ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
              <Loader />
            </Box>
              )
            : feedPosts.length === 0
              ? (
            <Stack spacing="sm" align="center" py="xl">
              <Text sx={{ textAlign: 'center', color: 'var(--gs-muted)', maxWidth: 480 }}>
                No long-form videos here yet. Upload with the orange button above, or check{' '}
                <strong>Discover</strong> for shoots you posted from the top navigation.
              </Text>
            </Stack>
                )
              : (
            <>
              <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md" sx={{ marginBottom: 40 }}>
                {feedPosts.map((post) => (
                  <Card
                    key={post.id}
                    onClick={() => setSelectedPost(post)}
                    sx={{
                      cursor: 'pointer',
                      background: 'var(--gs-surface2)',
                      border: '1px solid var(--gs-border)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: 'var(--gs-accent)',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    <Card.Section
                      sx={{
                        background: 'var(--gs-surface1)',
                        height: 200,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative'
                      }}
                    >
                      {post.isEmbedded
                        ? (
                        <Text sx={{ fontSize: 12, color: 'var(--gs-muted)' }}>Embedded Video</Text>
                          )
                        : post.thumbnailUrl
                          ? (
                        <img
                          src={toAbsoluteMediaUrl(post.thumbnailUrl)}
                          alt={post.title ?? ''}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                            )
                          : (
                        <Text sx={{ fontSize: 12, color: 'var(--gs-muted)' }}>No Thumbnail</Text>
                            )}
                    </Card.Section>

                    <Stack gap="xs" p="md">
                      <Group position="apart" align="flex-start">
                        <Box>
                          <Text
                            sx={{
                              fontSize: 14,
                              fontWeight: 600,
                              color: 'var(--gs-text)'
                            }}
                          >
                            {post.title}
                          </Text>
                          <Text
                            sx={{
                              fontSize: 12,
                              color: 'var(--gs-muted)',
                              marginTop: 4
                            }}
                          >
                            by {post.username}
                          </Text>
                        </Box>
                      </Group>

                      <Group position="apart">
                        <Group spacing="xs">
                          <Button
                            variant="subtle"
                            size="xs"
                            onClick={(e) => { e.stopPropagation(); requireAuth(() => handleToggleLike(post.id)) }}
                            leftIcon={<FiHeart size={14} fill={post.isLikedByCurrentUser ? 'currentColor' : 'none'} />}
                            sx={{ color: post.isLikedByCurrentUser ? 'var(--gs-accent)' : 'var(--gs-muted)' }}
                          >
                            {post.likeCount}
                          </Button>
                          <Button
                            variant="subtle"
                            size="xs"
                            onClick={(e) => { e.stopPropagation(); setSelectedPost(post) }}
                            leftIcon={<FiMessageSquare size={14} />}
                            sx={{ color: 'var(--gs-muted)' }}
                          >
                            {post.commentCount}
                          </Button>
                          <Button
                            variant="subtle"
                            size="xs"
                            onClick={(e) => { e.stopPropagation(); handleShare(post) }}
                            leftIcon={<FiShare2 size={14} />}
                            sx={{ color: 'var(--gs-muted)' }}
                          />
                        </Group>

                        {post.fileSizeMb && (
                          <Badge size="sm" variant="light" sx={{ fontFamily: 'DM Mono, monospace', fontSize: 10 }}>
                            {post.fileSizeMb} MB
                          </Badge>
                        )}
                      </Group>

                      <Text sx={{ fontSize: 11, color: 'var(--gs-muted)' }}>{formatDate(post.createdAt)}</Text>
                    </Stack>
                  </Card>
                ))}
              </SimpleGrid>

              <div ref={sentinelRef} style={{ height: '20px' }} />
              {feedLoading && <Loader style={{ margin: '0 auto' }} />}
            </>
                )}
        </Tabs.Panel>

        {/* Embed Tab */}
        {isAdmin && (
          <Tabs.Panel value="embed">
            <Card
              sx={{
                background: 'var(--gs-surface2)',
                border: '1px solid var(--gs-border)',
                maxWidth: 500
              }}
            >
              <Stack gap="md">
                {embedError && (
                  <Alert icon={<FiInfo />} title="Error" color="red">
                    {embedError}
                  </Alert>
                )}

                <TextInput
                  label="Embed URL"
                  placeholder="https://youtube.com/watch?v=..."
                  value={embedUrl}
                  onChange={(e) => setEmbedUrl(e.currentTarget.value)}
                  disabled={embedLoading}
                />

                <TextInput
                  label="Title"
                  placeholder="Video title"
                  value={embedTitle}
                  onChange={(e) => setEmbedTitle(e.currentTarget.value)}
                  disabled={embedLoading}
                />

                <Textarea
                  label="Description (optional)"
                  placeholder="Brief description"
                  value={embedDescription}
                  onChange={(e) => setEmbedDescription(e.currentTarget.value)}
                  minRows={3}
                  disabled={embedLoading}
                />

                <Button onClick={handleAddEmbed} loading={embedLoading}>
                  Add Embed
                </Button>
              </Stack>
            </Card>
          </Tabs.Panel>
        )}
      </Tabs>

      {/* Detail Modal */}
      <Modal opened={selectedPost !== null} onClose={() => setSelectedPost(null)} size="lg" title={selectedPost?.title}>
        {selectedPost && (
          <Stack gap="md">
            {selectedPost.isEmbedded
              ? (
              <Box
                sx={{
                  width: '100%',
                  paddingBottom: '56.25%',
                  position: 'relative',
                  background: 'var(--gs-surface1)'
                }}
              >
                <iframe
                  src={selectedPost.embedSourceUrl ?? ''}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    borderRadius: '4px'
                  }}
                  allowFullScreen
                />
              </Box>
                )
              : (
              <video
                controls
                style={{
                  width: '100%',
                  borderRadius: '4px',
                  background: 'var(--gs-surface1)'
                }}
              >
                <source src={toAbsoluteMediaUrl(selectedPost.videoUrl)} />
              </video>
                )}

            <Stack gap="xs">
              <Text sx={{ fontSize: 14, color: 'var(--gs-muted)' }}>{selectedPost.description}</Text>
              <Group position="apart">
                <Stack gap={0}>
                  <Text sx={{ fontSize: 12, fontWeight: 600 }}>{selectedPost.username}</Text>
                  <Text sx={{ fontSize: 11, color: 'var(--gs-muted)' }}>{formatDate(selectedPost.createdAt)}</Text>
                </Stack>
              </Group>

              <Group position="apart" grow>
                <Button
                  variant="light"
                  leftIcon={<FiHeart size={16} fill={selectedPost.isLikedByCurrentUser ? 'currentColor' : 'none'} />}
                  onClick={() => requireAuth(() => handleToggleLike(selectedPost.id))}
                  sx={{ color: selectedPost.isLikedByCurrentUser ? 'var(--gs-accent)' : 'inherit' }}
                >
                  {selectedPost.likeCount} Like{selectedPost.likeCount !== 1 ? 's' : ''}
                </Button>

                <Button
                  variant="light"
                  leftIcon={<FiShare2 size={16} />}
                  onClick={() => handleShare(selectedPost)}
                >
                  {shareCopied ? 'Copied!' : 'Share'}
                </Button>

                {(selectedPost.userId === store.userStore.user?.id || store.userStore.user?.isAdmin) && (
                  <Button
                    color="red"
                    variant="light"
                    leftIcon={<FiTrash2 size={16} />}
                    onClick={() => setDeleteConfirmId(selectedPost.id)}
                  >
                    Delete
                  </Button>
                )}
              </Group>

              {/* ── Comment Section ── */}
              <Box sx={{ borderTop: '1px solid var(--gs-border)', paddingTop: 16 }}>
                <Text sx={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
                  {selectedPost.commentCount} Comment{selectedPost.commentCount !== 1 ? 's' : ''}
                </Text>

                {isLoggedIn
                  ? (
                  <Group align="flex-end" spacing="xs" sx={{ marginBottom: 16 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Textarea
                        placeholder="Add a comment…"
                        value={newComment}
                        onChange={(e) => setNewComment(e.currentTarget.value)}
                        autosize
                        minRows={1}
                        maxRows={4}
                        disabled={commentSubmitting}
                      />
                    </Box>
                    <Button
                      variant="light"
                      size="sm"
                      leftIcon={<FiSend size={14} />}
                      loading={commentSubmitting}
                      disabled={!newComment.trim()}
                      onClick={() => handleAddComment(selectedPost.id)}
                    >
                      Post
                    </Button>
                  </Group>
                    )
                  : (
                  <Alert
                    icon={<FiMessageSquare size={14} />}
                    sx={{ marginBottom: 12, fontSize: 13 }}
                  >
                    <Text component="span">
                      <Text
                        component="a"
                        onClick={() => history.push('/register')}
                        sx={{ color: 'var(--gs-accent)', cursor: 'pointer', textDecoration: 'underline' }}
                      >
                        Sign up
                      </Text>{' '}
                      or{' '}
                      <Text
                        component="a"
                        onClick={() => history.push('/login')}
                        sx={{ color: 'var(--gs-accent)', cursor: 'pointer', textDecoration: 'underline' }}
                      >
                        log in
                      </Text>{' '}
                      to comment.
                    </Text>
                  </Alert>
                    )}

                {commentsLoading ? (
                  <Loader size="sm" style={{ display: 'block', margin: '0 auto' }} />
                ) : comments.length === 0 ? (
                  <Text sx={{ fontSize: 12, color: 'var(--gs-muted)', textAlign: 'center', padding: '8px 0' }}>
                    No comments yet. Be the first!
                  </Text>
                ) : (
                  <Stack gap="sm">
                    {comments.map((comment) => (
                      <Box key={comment.id}>
                        {/* Top-level comment */}
                        <Box
                          sx={{
                            background: 'var(--gs-surface1)',
                            borderRadius: 6,
                            padding: '10px 12px'
                          }}
                        >
                          <Group position="apart" align="flex-start">
                            <Box sx={{ flex: 1 }}>
                              <Group spacing="xs">
                                <Text sx={{ fontSize: 12, fontWeight: 600, color: 'var(--gs-accent)' }}>
                                  {comment.authorUsername}
                                </Text>
                                <Text sx={{ fontSize: 11, color: 'var(--gs-muted)' }}>
                                  {formatDate(comment.createdAt)}
                                </Text>
                              </Group>
                              <Text sx={{ fontSize: 13, marginTop: 4, wordBreak: 'break-word' }}>
                                {comment.body}
                              </Text>
                            </Box>
                            <Group spacing={4}>
                              {isLoggedIn && (
                                <Button
                                  variant="subtle"
                                  size="xs"
                                  compact
                                  leftIcon={<FiCornerDownRight size={12} />}
                                  sx={{ color: 'var(--gs-muted)', fontSize: 11 }}
                                  onClick={() => setReplyingToId(replyingToId === comment.id ? null : comment.id)}
                                >
                                  Reply
                                </Button>
                              )}
                              {(comment.authorUsername === store.userStore.user?.userName || isAdmin) && (
                                <Button
                                  variant="subtle"
                                  size="xs"
                                  compact
                                  leftIcon={<FiTrash2 size={12} />}
                                  sx={{ color: 'var(--gs-muted)' }}
                                  onClick={() => handleDeleteComment(selectedPost.id, comment.id)}
                                />
                              )}
                            </Group>
                          </Group>

                          {/* Inline reply form */}
                          {replyingToId === comment.id && (
                            <Group align="flex-end" spacing="xs" sx={{ marginTop: 8 }}>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Textarea
                                  placeholder={`Reply to ${comment.authorUsername}…`}
                                  value={replyText}
                                  onChange={(e) => setReplyText(e.currentTarget.value)}
                                  autosize
                                  minRows={1}
                                  maxRows={3}
                                  disabled={commentSubmitting}
                                />
                              </Box>
                              <Button
                                variant="light"
                                size="xs"
                                leftIcon={<FiSend size={12} />}
                                loading={commentSubmitting}
                                disabled={!replyText.trim()}
                                onClick={() => handleAddReply(selectedPost.id, comment.id)}
                              >
                                Reply
                              </Button>
                            </Group>
                          )}
                        </Box>

                        {/* Replies */}
                        {comment.replies.length > 0 && (
                          <Stack gap={4} sx={{ marginLeft: 24, marginTop: 4 }}>
                            {comment.replies.map((reply) => (
                              <Box
                                key={reply.id}
                                sx={{
                                  background: 'var(--gs-surface2)',
                                  borderRadius: 6,
                                  padding: '8px 12px',
                                  borderLeft: '2px solid var(--gs-border)'
                                }}
                              >
                                <Group position="apart" align="flex-start">
                                  <Box sx={{ flex: 1 }}>
                                    <Group spacing="xs">
                                      <Text sx={{ fontSize: 12, fontWeight: 600, color: 'var(--gs-accent)' }}>
                                        {reply.authorUsername}
                                      </Text>
                                      <Text sx={{ fontSize: 11, color: 'var(--gs-muted)' }}>
                                        {formatDate(reply.createdAt)}
                                      </Text>
                                    </Group>
                                    <Text sx={{ fontSize: 13, marginTop: 4, wordBreak: 'break-word' }}>
                                      {reply.body}
                                    </Text>
                                  </Box>
                                  {(reply.authorUsername === store.userStore.user?.userName || isAdmin) && (
                                    <Button
                                      variant="subtle"
                                      size="xs"
                                      compact
                                      leftIcon={<FiTrash2 size={12} />}
                                      sx={{ color: 'var(--gs-muted)' }}
                                      onClick={() => handleDeleteComment(selectedPost.id, reply.id, comment.id)}
                                    />
                                  )}
                                </Group>
                              </Box>
                            ))}
                          </Stack>
                        )}
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>
          </Stack>
        )}
      </Modal>

      {/* Delete Confirmation Dialog */}
      <Dialog opened={deleteConfirmId !== null} onClose={() => setDeleteConfirmId(null)}>
        <Stack gap="md">
          <Text>Are you sure you want to delete this post?</Text>
          <Group position="right">
            <Button variant="default" onClick={() => setDeleteConfirmId(null)}>
              Cancel
            </Button>
            <Button
              color="red"
              onClick={() => deleteConfirmId && handleDeletePost(deleteConfirmId)}
              loading={deleteLoading}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Dialog>

      {/* Upload Modal */}
      <Modal opened={uploadModalOpen} onClose={() => setUploadModalOpen(false)} title="Upload to Slop Feed" size="lg">
        <Stack gap="md">
          {uploadError && (
            <Alert icon={<FiInfo />} title="Error" color="red">
              {uploadError}
            </Alert>
          )}

          <FileInput
            label="Video File"
            placeholder="Select MP4, MOV, or WebM (max 150 MB)"
            accept="video/mp4,video/quicktime,video/webm"
            value={videoFile}
            onChange={setVideoFile}
            disabled={uploadLoading}
          />

          <TextInput
            label="Title"
            placeholder="What are you shooting?"
            value={uploadTitle}
            onChange={(e) => setUploadTitle(e.currentTarget.value)}
            disabled={uploadLoading}
          />

          <Textarea
            label="Description (optional)"
            placeholder="Details about the setup, specs, etc."
            value={uploadDescription}
            onChange={(e) => setUploadDescription(e.currentTarget.value)}
            minRows={3}
            disabled={uploadLoading}
          />

          {videoFile && videoFile.size > MAX_SLOP_VIDEO_BYTES && (
            <Alert icon={<FiInfo />} title="File Too Large" color="red">
              Video exceeds 150 MB limit. Please choose a smaller file.
            </Alert>
          )}

          {/* Consent Section */}
          <Box
            sx={{
              background: 'var(--gs-surface2)',
              border: '1px solid var(--gs-border)',
              borderRadius: 6,
              padding: 12
            }}
          >
            <Text
              sx={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--gs-text)',
                marginBottom: 8
              }}
            >
              Consent Terms {consentText && `(v${consentText.Version})`}
            </Text>
            {consentLoading
              ? (
              <Loader size="sm" />
                )
              : consentText
                ? (
              <>
                <Text
                  sx={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: 11,
                    color: 'var(--gs-muted)',
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.6,
                    marginBottom: 12
                  }}
                >
                  {consentText.Body}
                </Text>

                <Checkbox
                  label="I have read and agree to the above terms"
                  checked={consentGiven}
                  onChange={(e) => setConsentGiven(e.currentTarget.checked)}
                  disabled={uploadLoading}
                />
              </>
                  )
                : (
              <Text sx={{ fontSize: 11, color: 'var(--gs-muted)' }}>
                Unable to load consent terms. Please try again later.
              </Text>
                  )}
          </Box>

          {uploadLoading && uploadProgress > 0 && (
            <Progress value={uploadProgress} label={`${uploadProgress}%`} />
          )}

          {/* Actions */}
          <Group position="right">
            <Button variant="default" onClick={() => setUploadModalOpen(false)} disabled={uploadLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={
                !videoFile ||
                !uploadTitle.trim() ||
                !consentGiven ||
                uploadLoading ||
                (videoFile?.size ?? 0) > MAX_SLOP_VIDEO_BYTES
              }
              loading={uploadLoading}
              leftIcon={<FiUpload size={14} />}
            >
              {uploadLoading ? 'Uploading...' : 'Upload'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  )
}
